import React from 'react';
import { formatRequestedOn } from 'utils/dateUtils';
import {
  Box,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
  SxProps,
  Theme,
  CircularProgress,
} from '@mui/material';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { ReactComponent as WeightSvg } from 'assets/icons/Weight.svg';
import { ReactComponent as WeightRequestedSvg } from 'assets/icons/WeightTrackerRequested.svg';
import { SvgIcon } from 'components/SvgIcon/SvgIcon';
import { WidgetCard } from '../WidgetCard/WidgetCard';
import { WeightChart } from './WeightChart';

const STATINSET_XS = 4.5;
const STATINSET_SM = 6;
const CHART_MARGIN_XS = { top: 16, right: 8, bottom: 32, left: 44 };
const CHART_MARGIN_SM = { top: 24, right: 16, bottom: 35, left: 56 };
const CHART_FONT_XS = 12;
const CHART_FONT_SM = 16;

const StatBlock = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.25),
  marginBottom: theme.spacing(1),
  marginLeft: theme.spacing(STATINSET_XS),
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(STATINSET_SM),
  },
}));

const ValueRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: theme.spacing(0.5),
}));

const RequestedState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(1, 2, 2),
}));

const RequestedIllustration = styled(Box)(({ theme }) => ({
  color: theme.palette.grey[500],
  '& svg': { width: 72, height: 72 },
  [theme.breakpoints.up('sm')]: {
    '& svg': { width: 88, height: 88 },
  },
}));

const CardSizer = styled(Box)({
  width: '100%',
  boxSizing: 'border-box',
});

const LoadingContainer = styled(Box)(() => ({
  flex: 1,
  minHeight: 120,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ChartArea = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 214,
  [theme.breakpoints.up('sm')]: {
    height: 321,
  },
  // Negative margin to align the chart with the Figma spec.
  marginInline: theme.spacing(-1),
}));

const StatValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[900],
  fontWeight: 700,
}));

const StatUnit = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[700],
}));

// grey[600] on white fails axe color-contrast.
const StatCaption = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[700],
}));

const Y_PADDING = 5;

const computeYBounds = (weights: (number | null)[]) => {
  const values = weights.filter((w): w is number => w != null);

  if (values.length === 0) {
    return { min: undefined, max: undefined };
  }

  return {
    min: Math.floor(Math.min(...values) - Y_PADDING),
    max: Math.ceil(Math.max(...values) + Y_PADDING),
  };
};

interface WeightCardWrapperProps {
  onClick: () => void;
  className?: string;
  showChevron?: boolean;
  headerSx?: SxProps<Theme>;
  children: React.ReactNode;
  showHeader?: boolean;
}

const WeightCardWrapper: React.FC<WeightCardWrapperProps> = ({
  onClick,
  className,
  showChevron = true,
  headerSx,
  children,
  showHeader = true,
}) => (
  <CardSizer className={className}>
    <WidgetCard
      icon={
        <SvgIcon
          component={WeightSvg}
          aria-hidden="true"
          focusable="false"
          sx={{ '& path': { fill: (theme) => theme.palette.secondary.main } }}
        />
      }
      title="Weight"
      showChevron={showChevron}
      onClick={onClick}
      headerSx={headerSx}
      showHeader={showHeader}
    >
      {children}
    </WidgetCard>
  </CardSizer>
);

export interface WeightWidgetRequestedCardProps {
  onClick: () => void;
  className?: string;
  requestedDate: string;
  programName?: string;
}

const WeightWidgetRequestedContent: React.FC<
  Pick<WeightWidgetRequestedCardProps, 'requestedDate' | 'programName'>
> = ({ requestedDate, programName = 'Weekly Weight Tracker' }) => {
  const { translation } = useTranslationContext();

  return (
    <RequestedState>
      <RequestedIllustration>
        <WeightRequestedSvg aria-hidden focusable={false} />
      </RequestedIllustration>
      <Typography
        variant="callout"
        sx={{ color: (theme) => theme.palette.grey[800], maxWidth: 220 }}
      >
        {translation('tracker_requested', {
          arg1: programName,
          arg2: formatRequestedOn(requestedDate),
        })}
      </Typography>
    </RequestedState>
  );
};

export const WeightWidgetRequestedCard: React.FC<WeightWidgetRequestedCardProps> = ({
  onClick,
  className,
  requestedDate,
  programName,
}) => (
  <WeightCardWrapper onClick={onClick} className={className} showChevron={false}>
    <WeightWidgetRequestedContent requestedDate={requestedDate} programName={programName} />
  </WeightCardWrapper>
);

export interface WeightWidgetCardProps {
  onClick: () => void;
  className?: string;
  /** X-axis labels (oldest to newest), e.g. ['4/19', ...]. */
  dates: string[];
  /** Reading values aligned to `dates`; null = missing reading. */
  weights: (number | null)[];
  /** Latest reading value shown at the top (e.g. 162). */
  value: number | string;
  unit?: string;
  caption?: string;
  yAxisMin?: number;
  yAxisMax?: number;
  showLastReading?: boolean;
  showHeader?: boolean;
  isLoading?: boolean;
  errorContent?: React.ReactNode;
  requestedTracker?: { programName: string; requestedOn: string };
}

export const WeightWidgetCard: React.FC<WeightWidgetCardProps> = ({
  onClick,
  className,
  dates,
  weights,
  value,
  unit = 'lbs',
  caption = 'Last Reading',
  showLastReading = true,
  showHeader = true,
  yAxisMin = computeYBounds(weights).min,
  yAxisMax = computeYBounds(weights).max,
  isLoading = false,
  errorContent,
  requestedTracker,
}) => {
  const theme = useTheme();
  const { translation } = useTranslationContext();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const chartMargin = isSmUp ? CHART_MARGIN_SM : CHART_MARGIN_XS;
  const labelFontSize = isSmUp ? CHART_FONT_SM : CHART_FONT_XS;
  const hasError = errorContent !== undefined && errorContent !== null;
  const hasReadings = weights.some((weight) => weight != null);

  const renderCardContent = (): React.ReactNode => {
    if (isLoading) {
      return (
        <LoadingContainer>
          <CircularProgress size={24} aria-label="Loading weight data" />
        </LoadingContainer>
      );
    }

    if (hasError) {
      return errorContent;
    }

    if (requestedTracker) {
      return (
        <WeightWidgetRequestedContent
          requestedDate={formatRequestedOn(requestedTracker.requestedOn)}
          programName={requestedTracker.programName}
        />
      );
    }

    const displayCaption = hasReadings ? caption : translation('no_readings');

    return (
      <>
        {showLastReading && (
          <StatBlock>
            <ValueRow>
              <StatValue variant="title2">{hasReadings ? value : '--'}</StatValue>
              {unit && <StatUnit variant="callout">{unit}</StatUnit>}
            </ValueRow>
            {displayCaption && <StatCaption variant="footnote">{displayCaption}</StatCaption>}
          </StatBlock>
        )}
        <ChartArea>
          <WeightChart
            height="100%"
            dates={dates}
            weights={weights}
            yAxisMin={yAxisMin}
            yAxisMax={yAxisMax}
            margin={chartMargin}
            labelFontSize={labelFontSize}
          />
        </ChartArea>
      </>
    );
  };

  return (
    <WeightCardWrapper
      onClick={hasError ? () => undefined : onClick}
      className={className}
      showHeader={showHeader}
      showChevron={!requestedTracker || isLoading || hasError}
    >
      {renderCardContent()}
    </WeightCardWrapper>
  );
};

export default WeightWidgetCard;
